import { useState, useEffect, useCallback } from 'react';
import posthog from 'posthog-js';
import { markCookieConsentAccepted } from '@/components/CookieConsent';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { auth, authPersistenceReady, db, isFirebaseConfigured } from '@/lib/firebase';

type AppRole = 'candidate' | 'employer' | 'admin' | 'superadmin';
const GOOGLE_ROLE_KEY = 'google_signin_role';
const GOOGLE_RETURN_URL_KEY = 'google_signin_return_url';
const GOOGLE_REDIRECT_ERROR_KEY = 'google_redirect_error';
const GOOGLE_POST_AUTH_DESTINATION_KEY = 'google_post_auth_destination';

const GENERIC_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com',
  'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
  'zoho.com', 'yandex.com', 'live.com', 'msn.com',
  'rediffmail.com', 'tutanota.com',
];
const EMPLOYER_EMAIL_EXCEPTIONS = (import.meta.env.VITE_SUPER_ADMIN_EMAIL ? [import.meta.env.VITE_SUPER_ADMIN_EMAIL as string] : []);

function isAllowedEmployerSignupEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  if (EMPLOYER_EMAIL_EXCEPTIONS.includes(normalized)) return true;
  const domain = normalized.split('@')[1];
  return !!domain && !GENERIC_EMAIL_DOMAINS.includes(domain);
}

// Extended user type that provides Supabase-compatible properties
export interface User extends FirebaseUser {
  id: string; // Alias for uid (Supabase compatibility)
  user_metadata?: {
    full_name?: string;
    name?: string;
    avatar_url?: string;
  };
}

// Wrap Firebase user to add compatibility properties
function wrapUser(firebaseUser: FirebaseUser | null): User | null {
  if (!firebaseUser) return null;

  return Object.assign(firebaseUser, {
    id: firebaseUser.uid,
    user_metadata: {
      full_name: firebaseUser.displayName || undefined,
      name: firebaseUser.displayName || undefined,
      avatar_url: firebaseUser.photoURL || undefined,
    },
  }) as User;
}

const getReadableGoogleRedirectError = (error: unknown) => {
  const authError = error as { code?: string; message?: string };
  const code = authError.code || authError.message || '';

  if (typeof code === 'string' && code.includes('missing initial state')) {
    return 'Google sign-in could not be resumed on this browser. Please try again.';
  }
  if (typeof code === 'string' && code.includes('network-request-failed')) {
    return 'Network error while finishing Google sign-in. Please try again.';
  }

  return authError.message || 'Google sign-in failed. Please try again.';
};

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState<AppRole | null>(() => {
    return localStorage.getItem('intellr_active_role') as AppRole | null;
  });
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);

  useEffect(() => {
    // If Firebase is not configured, skip auth initialization
    // and render the app in "no-auth" mode (landing page still shows)
    if (!isFirebaseConfigured) {
      setLoading(false);
      return;
    }

    let authResolved = false;
    let redirectResolved = false;

    // Only block loading on the redirect result if a Google redirect was actually initiated.
    // On normal page refreshes (no pending OAuth), we resolve as soon as auth state fires.
    const isGoogleRedirectPending = !!localStorage.getItem(GOOGLE_ROLE_KEY);

    const finishLoadingIfReady = () => {
      if (authResolved && (redirectResolved || !isGoogleRedirectPending)) {
        setLoading(false);
      }
    };

    // Handle Google redirect result centrally here.
    // This is the single owner of redirect completion for both /auth and /auth/callback.
    authPersistenceReady
      .then(async () => {
        try {
          const result = await getRedirectResult(auth);
          if (!result?.user) return;

          const firebaseUser = result.user;
          const role = localStorage.getItem(GOOGLE_ROLE_KEY) as AppRole | null;
          localStorage.removeItem(GOOGLE_ROLE_KEY);
          const returnUrl = localStorage.getItem(GOOGLE_RETURN_URL_KEY);

          if (role === 'employer' && !isAllowedEmployerSignupEmail(firebaseUser.email)) {
            await firebaseSignOut(auth);
            localStorage.removeItem(GOOGLE_ROLE_KEY);
            localStorage.removeItem(GOOGLE_RETURN_URL_KEY);
            localStorage.removeItem(GOOGLE_POST_AUTH_DESTINATION_KEY);
            localStorage.setItem(GOOGLE_REDIRECT_ERROR_KEY, 'Employer accounts require a company email.');
            return;
          }

          // Profile creation with proper error handling
          try {
            const profileDoc = await getDoc(doc(db, 'profiles', firebaseUser.uid));
            if (!profileDoc.exists()) {
              await setDoc(doc(db, 'profiles', firebaseUser.uid), {
                user_id: firebaseUser.uid,
                email: firebaseUser.email,
                full_name: firebaseUser.displayName,
                avatar_url: firebaseUser.photoURL,
                phone: firebaseUser.phoneNumber,
                headline: null, bio: null, location: null, resume_url: null,
                linkedin_url: null, github_url: null, portfolio_url: null,
                skills: [], preferred_locations: [], work_types: [],
                experience_years: null, salary_min: null, salary_max: null,
                industry: null, career_goals: null, work_history: [], education: [],
                parsed_resume_data: {}, interview_insights: [],
                profile_completed: false,
                created_at: serverTimestamp(), updated_at: serverTimestamp(),
              });
              await setDoc(doc(db, 'user_roles', firebaseUser.uid), {
                user_id: firebaseUser.uid,
                roles: [role || 'candidate'],
                created_at: serverTimestamp(),
              });

              const onboardingBase = role === 'employer' ? '/employer/onboarding' : '/candidate/onboarding';
              const postAuthDestination = returnUrl
                ? `${onboardingBase}?returnUrl=${encodeURIComponent(returnUrl)}`
                : onboardingBase;
              localStorage.setItem(GOOGLE_POST_AUTH_DESTINATION_KEY, postAuthDestination);
            } else {
              // Existing user: determine destination from returnUrl or roles
              if (returnUrl) {
                localStorage.setItem(GOOGLE_POST_AUTH_DESTINATION_KEY, returnUrl);
              } else {
                const rolesDoc = await getDoc(doc(db, 'user_roles', firebaseUser.uid));
                const roles = rolesDoc.exists() ? (rolesDoc.data().roles || []) : [];
                const dest = roles.includes('employer') ? '/employer/dashboard' : '/candidate/dashboard';
                localStorage.setItem(GOOGLE_POST_AUTH_DESTINATION_KEY, dest);
              }
            }

            localStorage.removeItem(GOOGLE_RETURN_URL_KEY);
          } catch (firestoreErr) {
            // Profile creation failed - log and set error
            console.error('[Auth] Profile creation failed after Google sign-in:', firestoreErr);
            const errorMsg = firestoreErr instanceof Error
              ? `Failed to create your profile: ${firestoreErr.message}. Please try signing in again.`
              : 'Failed to create your profile. Please try signing in again.';
            localStorage.setItem(GOOGLE_REDIRECT_ERROR_KEY, errorMsg);
            localStorage.removeItem(GOOGLE_POST_AUTH_DESTINATION_KEY);
            localStorage.removeItem(GOOGLE_RETURN_URL_KEY);
            // Sign out to force re-auth and retry
            await firebaseSignOut(auth);
          }
        } catch (redirectErr) {
          // Redirect result errors (network, missing state, etc.)
          console.error('[Auth] Redirect error:', redirectErr);
          localStorage.removeItem(GOOGLE_POST_AUTH_DESTINATION_KEY);
          // Only set error if it's not a Firebase config issue
          const errMsg = (redirectErr as any)?.code || (redirectErr as any)?.message || '';
          if (!errMsg.includes('invalid-api-key') && !errMsg.includes('invalid-app-credential')) {
            localStorage.setItem(GOOGLE_REDIRECT_ERROR_KEY, getReadableGoogleRedirectError(redirectErr));
          }
        }
      })
      .catch((err: unknown) => {
        // authPersistenceReady promise rejection
        console.error('[Auth] Persistence ready error:', err);
        localStorage.removeItem(GOOGLE_POST_AUTH_DESTINATION_KEY);
        // Only set error if it's not a Firebase config issue
        const errMsg = (err as any)?.code || (err as any)?.message || '';
        if (!errMsg.includes('invalid-api-key') && !errMsg.includes('invalid-app-credential')) {
          localStorage.setItem(GOOGLE_REDIRECT_ERROR_KEY, getReadableGoogleRedirectError(err));
        }
      })
      .finally(() => {
        redirectResolved = true;
        finishLoadingIfReady();
      });

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(wrapUser(firebaseUser));
      // PostHog identity
      if (firebaseUser) {
        posthog.identify(firebaseUser.uid, {
          email: firebaseUser.email ?? undefined,
          name: firebaseUser.displayName ?? undefined,
        });
      } else {
        posthog.reset();
      }
      authResolved = true;
      finishLoadingIfReady();
    }, (error) => {
      // Auth state change error (e.g., invalid API key) - gracefully handle
      console.warn('[Auth] Firebase auth error:', error.code);
      setUser(null);
      posthog.reset();
      authResolved = true;
      finishLoadingIfReady();
    });

    return () => unsubscribe();
  }, []);


  const signUp = async (email: string, password: string, fullName: string, role: AppRole) => {
    if (role === 'employer' && !isAllowedEmployerSignupEmail(email)) {
      throw new Error('Employer accounts require a company email.');
    }
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    try {
      // Update display name
      await updateProfile(firebaseUser, { displayName: fullName });

      // Create profile document
      await setDoc(doc(db, 'profiles', firebaseUser.uid), {
        user_id: firebaseUser.uid,
        email: firebaseUser.email,
        full_name: fullName,
        avatar_url: null,
        phone: null,
        headline: null,
        bio: null,
        location: null,
        resume_url: null,
        linkedin_url: null,
        github_url: null,
        portfolio_url: null,
        skills: [],
        preferred_locations: [],
        work_types: [],
        experience_years: null,
        salary_min: null,
        salary_max: null,
        industry: null,
        career_goals: null,
        work_history: [],
        education: [],
        parsed_resume_data: {},
        interview_insights: [],
        profile_completed: false,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });

      // Create user roles document
      await setDoc(doc(db, 'user_roles', firebaseUser.uid), {
        user_id: firebaseUser.uid,
        roles: [role],
        created_at: serverTimestamp(),
      });

      // Send email verification
      await sendEmailVerification(firebaseUser);

      // Cookie consent: captured via ToS acceptance at sign-up
      markCookieConsentAccepted();

      // PostHog signup event
      posthog.capture(role === 'employer' ? 'employer_signed_up' : 'candidate_signed_up', {
        role,
        email: firebaseUser.email,
      });

      return { user: wrapUser(firebaseUser) };
    } catch (firestoreErr) {
      // Profile creation failed - clean up auth user and throw descriptive error
      console.error('[Auth] Profile creation failed during email signup:', firestoreErr);
      await firebaseUser.delete();
      throw new Error('Failed to create your profile. Please try again.');
    }
  };

  const signIn = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: wrapUser(userCredential.user) };
  };

  const signInWithGoogle = async (role?: AppRole, returnUrl?: string) => {
    const provider = new GoogleAuthProvider();

    localStorage.removeItem(GOOGLE_REDIRECT_ERROR_KEY);
    if (role) localStorage.setItem(GOOGLE_ROLE_KEY, role);
    else localStorage.removeItem(GOOGLE_ROLE_KEY);
    if (returnUrl) localStorage.setItem(GOOGLE_RETURN_URL_KEY, returnUrl);
    else localStorage.removeItem(GOOGLE_RETURN_URL_KEY);

    let result;
    try {
      result = await signInWithPopup(auth, provider);
    } catch (popupErr: any) {
      // If popup blocked or COOP issue, fall back to redirect
      if (popupErr.code === 'auth/popup-blocked' ||
        popupErr.code === 'auth/popup-closed-by-user' ||
        popupErr.code === 'auth/cancelled-popup-request' ||
        popupErr.code === 'auth/invalid-credential') {
        await signInWithRedirect(auth, provider);
        return { user: null, isNewUser: false }; // Page will reload after redirect
      }
      throw popupErr;
    }

    const firebaseUser = result.user;
    if (role === 'employer' && !isAllowedEmployerSignupEmail(firebaseUser.email)) {
      await firebaseSignOut(auth);
      localStorage.removeItem(GOOGLE_ROLE_KEY);
      localStorage.removeItem(GOOGLE_RETURN_URL_KEY);
      throw new Error('Employer accounts require a company email.');
    }

    // Check if profile exists
    const profileDoc = await getDoc(doc(db, 'profiles', firebaseUser.uid));

    if (!profileDoc.exists()) {
      // Create profile for new Google users
      await setDoc(doc(db, 'profiles', firebaseUser.uid), {
        user_id: firebaseUser.uid,
        email: firebaseUser.email,
        full_name: firebaseUser.displayName,
        avatar_url: firebaseUser.photoURL,
        phone: firebaseUser.phoneNumber,
        headline: null,
        bio: null,
        location: null,
        resume_url: null,
        linkedin_url: null,
        github_url: null,
        portfolio_url: null,
        skills: [],
        preferred_locations: [],
        work_types: [],
        experience_years: null,
        salary_min: null,
        salary_max: null,
        industry: null,
        career_goals: null,
        work_history: [],
        education: [],
        parsed_resume_data: {},
        interview_insights: [],
        profile_completed: false,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });

      // Create user roles
      await setDoc(doc(db, 'user_roles', firebaseUser.uid), {
        user_id: firebaseUser.uid,
        roles: [role || 'candidate'],
        created_at: serverTimestamp(),
      });
    }

    localStorage.removeItem(GOOGLE_ROLE_KEY);
    localStorage.removeItem(GOOGLE_RETURN_URL_KEY);

    // Cookie consent captured via ToS acceptance on sign-in with Google
    markCookieConsentAccepted();

    // PostHog signup event (only for new users)
    if (!profileDoc.exists()) {
      posthog.capture(role === 'employer' ? 'employer_signed_up' : 'candidate_signed_up', {
        role: role || 'candidate',
        email: firebaseUser.email,
        method: 'google',
      });
    }

    return { user: wrapUser(firebaseUser), isNewUser: !profileDoc.exists() };
  };

  const setupRecaptcha = (elementId: string) => {
    const recaptchaVerifier = new RecaptchaVerifier(auth, elementId, {
      size: 'invisible',
      callback: () => { },
    });
    return recaptchaVerifier;
  };

  const sendPhoneOTP = async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) => {
    const result = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    setConfirmationResult(result);
    return result;
  };

  const verifyPhoneOTP = async (otp: string, role?: AppRole, fullName?: string) => {
    if (!confirmationResult) {
      throw new Error('No confirmation result. Send OTP first.');
    }

    const result = await confirmationResult.confirm(otp);
    const firebaseUser = result.user;
    if (role === 'employer' && !isAllowedEmployerSignupEmail(firebaseUser.email)) {
      await firebaseSignOut(auth);
      throw new Error('Employer accounts require a company email.');
    }

    // Check if profile exists
    const profileDoc = await getDoc(doc(db, 'profiles', firebaseUser.uid));

    if (!profileDoc.exists()) {
      // Create profile for new phone users
      await setDoc(doc(db, 'profiles', firebaseUser.uid), {
        user_id: firebaseUser.uid,
        email: firebaseUser.email,
        full_name: fullName || null,
        avatar_url: null,
        phone: firebaseUser.phoneNumber,
        headline: null,
        bio: null,
        location: null,
        resume_url: null,
        linkedin_url: null,
        github_url: null,
        portfolio_url: null,
        skills: [],
        preferred_locations: [],
        work_types: [],
        experience_years: null,
        salary_min: null,
        salary_max: null,
        industry: null,
        career_goals: null,
        work_history: [],
        education: [],
        parsed_resume_data: {},
        interview_insights: [],
        profile_completed: false,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });

      // Create user roles
      await setDoc(doc(db, 'user_roles', firebaseUser.uid), {
        user_id: firebaseUser.uid,
        roles: [role || 'candidate'],
        created_at: serverTimestamp(),
      });
    }

    return { user: wrapUser(firebaseUser), isNewUser: !profileDoc.exists() };
  };

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    setUser(null);

    // MED-009: Clear localStorage on logout to prevent session data leakage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.warn('Failed to clear storage on logout:', error);
    }

    window.location.href = '/';
  }, []);

  // HIGH-016 FIXED: Depend on user?.uid instead of user to prevent cascading re-renders
  const getUserRoles = useCallback(async (): Promise<AppRole[]> => {
    if (!user?.uid) return [];

    try {
      const rolesDoc = await getDoc(doc(db, 'user_roles', user.uid));
      if (rolesDoc.exists()) {
        return rolesDoc.data().roles || [];
      }
    } catch (err) {
      console.error('Error fetching user roles:', err);
    }
    return [];
  }, [user?.uid]);

  const getUserRole = useCallback(async (): Promise<AppRole | null> => {
    const roles = await getUserRoles();
    if (roles.length === 0) return null;

    // Use activeRole if it's still valid for the user
    if (activeRole && roles.includes(activeRole)) {
      return activeRole;
    }

    // Default prioritization
    let defaultRole: AppRole;
    if (roles.includes('superadmin')) defaultRole = 'superadmin';
    else if (roles.includes('employer')) defaultRole = 'employer';
    else if (roles.includes('candidate')) defaultRole = 'candidate';
    else defaultRole = roles[0];

    // Set as active if not already set correctly
    if (activeRole !== defaultRole) {
      setActiveRole(defaultRole);
      localStorage.setItem('intellr_active_role', defaultRole);
    }

    return defaultRole;
  }, [getUserRoles, activeRole]);

  const hasRole = useCallback(async (role: AppRole): Promise<boolean> => {
    const roles = await getUserRoles();
    return roles.includes(role);
  }, [getUserRoles]);

  const addRole = useCallback(async (role: AppRole): Promise<boolean> => {
    if (!user) return false;
    if (role === 'employer' && !isAllowedEmployerSignupEmail(user.email)) {
      throw new Error('Employer role requires a company email.');
    }

    const roles = await getUserRoles();
    if (roles.includes(role)) return true;

    await setDoc(doc(db, 'user_roles', user.uid), {
      user_id: user.uid,
      roles: arrayUnion(role),
      updated_at: serverTimestamp(),
    }, { merge: true });

    // Automatically switch to the newly added role
    setActiveRole(role);
    localStorage.setItem('intellr_active_role', role);

    return true;
  }, [user, getUserRoles]);

  const switchRole = useCallback((role: AppRole) => {
    setActiveRole(role);
    localStorage.setItem('intellr_active_role', role);
  }, []);

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const sendVerificationEmail = async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  };

  return {
    user,
    session: user ? { user } : null,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    setupRecaptcha,
    sendPhoneOTP,
    verifyPhoneOTP,
    signOut,
    getUserRole,
    getUserRoles,
    hasRole,
    addRole,
    switchRole,
    activeRole,
    resetPassword,
    sendVerificationEmail,
    confirmationResult,
  };
}
